/**
 * 通用js方法封装处理
 */

/**
 * 验证手机号码
 * @param {string} phone - 手机号码
 * @returns {boolean} 是否有效
 */
export const isValidMobile = (phone) => {
  if (!phone) return false;

  const cleanPhone = phone.replace(/\s+/g, "");

  // 中国手机号正则：1开头，第二位3-9，共11位
  const mobileRegex = /^1[3-9]\d{9}$/;

  return mobileRegex.test(cleanPhone);
};

/**
 * 验证邮箱
 * @param {string} email - 邮箱地址
 * @returns {boolean} 是否有效
 */
export const isValidEmail = (email) => {
  if (!email) return false;

  // 邮箱正则表达式
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return emailRegex.test(email);
};

/**
 * 验证身份证号
 * @param {string} idCard - 身份证号
 * @returns {boolean} 是否有效
 */
export const isValidIdCard = (idCard) => {
  if (!idCard) return false;

  // 去除空格
  const code = idCard.replace(/\s+/g, "").toUpperCase();

  // 1. 基础格式校验
  const reg = /(^\d{15}$)|(^\d{17}[\dXx]$)/;
  if (!reg.test(code)) return false;

  // 2. 如果是15位，直接返回（老身份证，无校验位）
  if (code.length === 15) return true;

  // 3. 18位身份证 → 校验最后一位校验码（核心！）
  const factor = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2]; // 加权因子
  const parity = [1, 0, "X", 9, 8, 7, 6, 5, 4, 3, 2]; // 校验位对应
  let sum = 0;

  for (let i = 0; i < 17; i++) {
    sum += parseInt(code[i]) * factor[i];
  }

  const mod = sum % 11;
  return code[17] === String(parity[mod]);
};

/**
 * Element UI 表单验证 - 电话
 * @param {Object} rule - 验证规则
 * @param {string} value - 验证值
 * @param {Function} callback - 回调函数
 */
export const validatePhone = (rule, value, callback) => {
  if (!value) {
    callback(new Error("请输入联系电话"));
  } else if (!isValidMobile(value)) {
    callback(new Error("请输入正确的联系电话"));
  } else {
    callback();
  }
};

/**
 * Element UI 表单验证 - 邮箱
 * @param {Object} rule - 验证规则
 * @param {string} value - 验证值
 * @param {Function} callback - 回调函数
 */
export const validateEmail = (rule, value, callback) => {
  if (!value) {
    callback(new Error("请输入邮箱"));
  } else if (!isValidEmail(value)) {
    callback(new Error("请输入正确的邮箱"));
  } else {
    callback();
  }
};

/**
 * Element UI 表单验证 - 身份证号
 * @param {Object} rule - 验证规则
 * @param {string} value - 验证值
 * @param {Function} callback - 回调函数
 */
export const validateIdCard = (rule, value, callback) => {
  if (!value) {
    callback(new Error("请输入身份证号"));
  } else if (!isValidIdCard(value)) {
    callback(new Error("请输入正确的身份证号"));
  } else {
    callback();
  }
};

// 日期格式化
export function parseTime(time, pattern) {
  if (arguments.length === 0 || !time) {
    return null;
  }
  const format = pattern || "{y}-{m}-{d} {h}:{i}:{s}";
  let date;
  if (typeof time === "object") {
    date = time;
  } else {
    if (typeof time === "string" && /^[0-9]+$/.test(time)) {
      time = parseInt(time);
    } else if (typeof time === "string") {
      time = time
        .replace(new RegExp(/-/gm), "/")
        .replace("T", " ")
        .replace(new RegExp(/\.[\d]{3}/gm), "");
    }
    if (typeof time === "number" && time.toString().length === 10) {
      time = time * 1000;
    }
    date = new Date(time);
  }
  const formatObj = {
    y: date.getFullYear(),
    m: date.getMonth() + 1,
    d: date.getDate(),
    h: date.getHours(),
    i: date.getMinutes(),
    s: date.getSeconds(),
    a: date.getDay(),
  };
  return format.replace(/{(y|m|d|h|i|s|a)+}/g, (result, key) => {
    let value = formatObj[key];
    // Note: getDay() returns 0 on Sunday
    if (key === "a") {
      return ["日", "一", "二", "三", "四", "五", "六"][value];
    }
    if (result.length > 0 && value < 10) {
      value = "0" + value;
    }
    return value || 0;
  });
}

/**
 * 构造树型结构数据
 * @param {*} data 数据源
 * @param {*} id id字段 默认 'id'
 * @param {*} parentId 父节点字段 默认 'parentId'
 * @param {*} children 孩子节点字段 默认 'children'
 */
export function handleTree(
  data,
  id = "id",
  parentId = "parentId",
  children = "children",
) {
  const config = {
    id: id || "id",
    parentId: parentId || "parentId",
    childrenList: children || "children",
  };

  var childrenListMap = {};
  var nodeIds = {};
  var tree = [];

  for (const d of data) {
    const parentId = d[config.parentId];
    if (childrenListMap[parentId] == null) {
      childrenListMap[parentId] = [];
    }
    nodeIds[d[config.id]] = d;
    childrenListMap[parentId].push(d);
  }

  for (const d of data) {
    const parentId = d[config.parentId];
    if (nodeIds[parentId] == null) {
      tree.push(d);
    }
  }

  for (const t of tree) {
    adaptToChildrenList(t);
  }

  function adaptToChildrenList(o) {
    if (childrenListMap[o[config.id]] !== null) {
      o[config.childrenList] = childrenListMap[o[config.id]];
    }
    if (o[config.childrenList]) {
      for (const c of o[config.childrenList]) {
        adaptToChildrenList(c);
      }
    }
  }
  return tree;
}

/**
 * 参数处理
 * @param {*} params  参数
 */
export function tansParams(params) {
  let result = "";
  for (const propName of Object.keys(params)) {
    const value = params[propName];
    var part = encodeURIComponent(propName) + "=";
    if (value !== null && value !== "" && typeof value !== "undefined") {
      if (typeof value === "object") {
        for (const key of Object.keys(value)) {
          if (
            value[key] !== null &&
            value[key] !== "" &&
            typeof value[key] !== "undefined"
          ) {
            const params = propName + "[" + key + "]";
            var subPart = encodeURIComponent(params) + "=";
            result += subPart + encodeURIComponent(value[key]) + "&";
          }
        }
      } else {
        result += part + encodeURIComponent(value) + "&";
      }
    }
  }
  return result;
}

// 判断给定的路径是否是外部链接
export function isExternal(path) {
  return /^(https?:|mailto:|tel:)/.test(path);
}

// 防抖函数
export function debounce(func, wait, immediate) {
  let timeout;
  return function executedFunction(...args) {
    const context = this;
    const later = () => {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

// 挂牌价显示
export function getPriceInfo(res, isDetail) {
  const {
    isFacediscuss,
    subjectPriceStr,
    subUnit,
    isBatch,
    listPriceContent,
    noticeType,
    bidType,
  } = res;

  const isOnce = bidType == "O";
  const zsText = noticeType === "zs" ? "详见公告" : "--";
  const priceLabel = noticeType === "zs" ? "转让底价" : "挂牌价格";

  if (isBatch && !isDetail) {
    return {
      label: priceLabel,
      text: "详见公告",
    };
  }

  if (isOnce) {
    return {
      label: priceLabel,
      text: "--",
    };
  }

  if (isFacediscuss == 0) {
    return {
      label: priceLabel,
      text: subjectPriceStr || zsText,
      unit: subUnit,
    };
  } else if (isFacediscuss == 1) {
    return {
      label: priceLabel,
      text: "面议",
    };
  } else if (isFacediscuss == 2) {
    return {
      label: priceLabel,
      text: subjectPriceStr || zsText,
    };
  } else if (isFacediscuss == 3) {
    return {
      label: priceLabel,
      text: listPriceContent || zsText,
    };
  }
}

// 拼接登录注册URL参数
export function buildAuthUrl(baseUrl, type) {
  if (!baseUrl) {
    return "";
  }

  try {
    const subtitle = 1;
    const oldPathUrl =
      type !== "member" ? window.location.href : process.env.VUE_APP_MEMBER_URL;
    const url = new URL(baseUrl);
    url.searchParams.append("subtitle", subtitle);
    url.searchParams.append("registerType", 6);

    if (oldPathUrl) {
      const authPrefix = process.env.VUE_APP_AUTH_URL_PREFIX || "";
      url.searchParams.append("oldPathUrl", authPrefix + oldPathUrl);
    }

    return url.toString();
  } catch (error) {
    console.error("构建认证URL时出错:", error);
    return baseUrl;
  }
}
